import { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';

const MONTHS = [
  { value: 1, label: '1月' },
  { value: 2, label: '2月' },
  { value: 3, label: '3月' },
  { value: 4, label: '4月' },
  { value: 5, label: '5月' },
  { value: 6, label: '6月' },
  { value: 7, label: '7月' },
  { value: 8, label: '8月' },
  { value: 9, label: '9月' },
  { value: 10, label: '10月' },
  { value: 11, label: '11月' },
  { value: 12, label: '12月' },
];

/**
 * 年月選擇元件
 * @param {string} value - 當前值 (YYYY-MM 或 YYYY-MM-DD 格式)
 * @param {function} onChange - 值改變回呼函式
 * @param {string} mode - 'month' 或 'date' 模式，決定輸出格式
 * @param {string} dayStrategy - 'first' 或 'last'，date 模式時輸出該月第一天或最後一天
 * @param {string} className - 額外的 CSS class
 * @param {string} placeholder - 佔位文字
 * @param {boolean} required - 是否必填
 * @param {string} label - 標籤文字
 * @param {number} minYear - 最小年份 (預設: 2020)
 * @param {number} maxYear - 最大年份 (預設: 當前年份 + 5)
 */
export default function YearMonthPicker({
  value = '',
  onChange,
  mode = 'month', // 'month' 或 'date'
  dayStrategy = 'first', // 'first' 或 'last'
  className = '',
  placeholder = '選擇年月',
  required = false,
  label = '',
  minYear = 2020,
  maxYear = new Date().getFullYear() + 5,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedMonthKey, setSelectedMonthKey] = useState(null);
  const [view, setView] = useState('months');
  const pickerRef = useRef(null);

  // 從 value 解析年月
  useEffect(() => {
    if (value) {
      const parts = value.split('-');
      if (parts.length >= 2) {
        setSelectedYear(parseInt(parts[0], 10));
        setSelectedMonth(parseInt(parts[1], 10));
        setSelectedDay(parts[2] ? parseInt(parts[2], 10) : null);
        setSelectedMonthKey(`${parts[0]}-${parts[1]}`);
      }
    } else {
      // 預設為當前年月
      const now = new Date();
      setSelectedYear(now.getFullYear());
      setSelectedMonth(now.getMonth() + 1);
      setSelectedDay(null);
      setSelectedMonthKey(null);
    }
  }, [value]);

  // 點擊外部關閉
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // 格式化顯示值
  const displayValue = () => {
    if (!value) return placeholder;
    const parts = value.split('-');
    if (parts.length >= 2) {
      const year = parts[0];
      const month = parts[1];
      if (mode === 'date' && parts.length === 3) {
        return `${year}-${month}-${parts[2]}`;
      }
      return `${year}-${month}`;
    }
    return placeholder;
  };

  // 處理年份改變：即時更新值，但不關閉選單
  const handleYearChange = (newYear) => {
    setSelectedYear(newYear);
    if (mode === 'date') {
      setSelectedMonthKey(null);
      setView('months');
    } else if (selectedMonth) {
      updateValue(newYear, selectedMonth, { close: false });
    }
  };

  // 日期模式下，先選月份可確認整月；再次點選同月才展開日期格。
  const handleMonthSelect = (month) => {
    const monthKey = `${selectedYear}-${month}`;

    if (mode === 'date' && selectedMonthKey === monthKey) {
      setView('days');
      return;
    }

    setSelectedMonth(month);
    setSelectedDay(null);
    setSelectedMonthKey(monthKey);
    setView('months');
    if (mode !== 'date') {
      updateValue(selectedYear, month, { close: true });
    }
  };

  // 確認使用整月並關閉選單。
  const handleConfirmMonth = () => {
    updateValue(selectedYear, selectedMonth, { close: true });
  };

  const handleDaySelect = (day) => {
    setSelectedDay(day);
    updateValue(selectedYear, selectedMonth, { day, close: true });
    setView('months');
  };

  // 更新值
  const updateValue = (year, month, { day = null, close = true } = {}) => {
    const monthStr = month.toString().padStart(2, '0');
    let newValue;

    if (mode === 'date') {
      // date 模式：依 dayStrategy 輸出該月第一天或最後一天
      const resolvedDay = day || (dayStrategy === 'last'
        ? new Date(year, month, 0).getDate()
        : 1);
      newValue = `${year}-${monthStr}-${String(resolvedDay).padStart(2, '0')}`;
    } else {
      // month 模式：輸出 YYYY-MM
      newValue = `${year}-${monthStr}`;
    }

    onChange(newValue);
    if (close) {
      setIsOpen(false);
    }
  };

  // 年份選項
  const yearOptions = [];
  for (let y = maxYear; y >= minYear; y--) {
    yearOptions.push(y);
  }

  return (
    <div className="relative" ref={pickerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* 輸入框 */}
      <div
        onClick={() => {
          const nextOpen = !isOpen;
          setIsOpen(nextOpen);
          if (nextOpen) {
            setView('months');
          }
        }}
        className={`relative w-full px-3 py-2 border border-gray-300 rounded-lg cursor-pointer focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent bg-white ${className}`}
      >
        <div className="flex items-center justify-between">
          <span className={value ? 'text-gray-900' : 'text-gray-400'}>
            {displayValue()}
          </span>
          <Calendar className="w-4 h-4 text-gray-400" />
        </div>
      </div>

      {/* 下拉選擇器 */}
      {isOpen && (
        <div className="absolute z-50 mt-2 w-full md:w-80 bg-white border border-gray-300 rounded-lg shadow-xl">
          {/* 年份選擇器 */}
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between space-x-2">
              <button
                type="button"
                onClick={() => handleYearChange(Math.max(selectedYear - 1, minYear))}
                disabled={selectedYear <= minYear}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>

              <select
                value={selectedYear || new Date().getFullYear()}
                onChange={(e) => handleYearChange(parseInt(e.target.value, 10))}
                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-center font-semibold text-gray-900 focus:ring-2 focus:ring-blue-500"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y} 年
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={() => handleYearChange(Math.min(selectedYear + 1, maxYear))}
                disabled={selectedYear >= maxYear}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>

          {view === 'months' && (
            <>
              {/* 月份選擇器 */}
              <div className="p-3">
                <div className="grid grid-cols-3 gap-2">
                  {MONTHS.map((month) => {
                const isSelected = selectedMonth === month.value;
                const isCurrent =
                  selectedYear === new Date().getFullYear() &&
                  month.value === new Date().getMonth() + 1;

                return (
                  <button
                    key={month.value}
                    type="button"
                    onClick={() => handleMonthSelect(month.value)}
                    className={`
                      px-3 py-2 rounded-lg text-sm font-medium transition-all
                      ${isSelected
                        ? 'bg-blue-500 text-white shadow-md'
                        : isCurrent
                        ? 'bg-blue-50 text-blue-700 border border-blue-300'
                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                      }
                    `}
                  >
                    {month.label}
                  </button>
                );
                  })}
                </div>
              </div>
            </>
          )}

          {mode === 'date' && view === 'days' && selectedYear && selectedMonth && (() => {
            const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
            const firstDay = new Date(selectedYear, selectedMonth - 1, 1).getDay();
            const dayLabels = ['日', '一', '二', '三', '四', '五', '六'];
            return (
              <div className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <button type="button" onClick={() => setView('months')} className="text-sm text-blue-600 hover:text-blue-800">返回月份</button>
                  <span className="text-sm font-semibold text-gray-700">{selectedYear} 年 {selectedMonth} 月</span>
                </div>
                <div className="grid grid-cols-7 gap-1 text-center text-xs text-gray-500 mb-1">
                  {dayLabels.map((label) => <span key={label}>{label}</span>)}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {Array.from({ length: firstDay }, (_, index) => <span key={`empty-${index}`} />)}
                  {Array.from({ length: daysInMonth }, (_, index) => {
                    const day = index + 1;
                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => handleDaySelect(day)}
                        className={`h-8 rounded text-sm ${selectedDay === day ? 'bg-blue-500 text-white' : 'text-gray-700 hover:bg-blue-50'}`}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* 整月確認；展開日期後，直接點日期即可完成。 */}
          {mode === 'date' && view === 'months' && selectedMonthKey === `${selectedYear}-${selectedMonth}` && (
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={handleConfirmMonth}
                className="w-full px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded hover:bg-green-100 transition-colors"
              >
                確認
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
